import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { PlusIcon } from '../lib/icons.tsx';

export interface DropzoneProps extends Omit<ComponentProps<'button'>, 'type' | 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  /** Replaces the default `+`. */
  icon?: ReactNode;
}

/**
 * The dashed tile that asks for a file — a logo, a product photo, a reference.
 *
 * Dashed and unfilled so it reads as a hole in the layout rather than as a
 * card; it takes the brand border on hover, the same signal an option card
 * gives.
 */
export function Dropzone({ title, description, icon, className, ...props }: DropzoneProps) {
  return (
    <button
      type="button"
      className={cn(
        'flex w-full cursor-pointer flex-col items-center justify-center gap-2 p-6',
        'rounded-card border-2 border-dashed border-border-subtle bg-surface',
        'text-bodysm font-medium text-ink-muted',
        'transition-[border-color,color] duration-150 ease-out-quint',
        'hover:border-brand-400 hover:text-ink',
        'disabled:cursor-not-allowed disabled:border-border-subtle disabled:text-ink-faint',
        className,
      )}
      {...props}
    >
      <span className="flex size-6 items-center justify-center text-ink-dim">
        {icon ?? <PlusIcon className="size-5" />}
      </span>
      {title}
      {description ? <span className="text-sm text-ink-faint">{description}</span> : null}
    </button>
  );
}
